import {
  prisma,
  OrderItemStatus,
  OrderStatus,
  ParticipantGroupType,
} from "client";
import {
  attendeeMatchesCampaign,
  buyerMatchesFilters,
  canonicalizeAudienceIds,
  collectAttendeeFromItems,
  countAudienceOverlap,
  includeBuyerPool,
  includeGroupPool,
  isEntityAudienceContact,
  mergeAudienceIds,
  pickEntityAudienceProfileIds,
  uniqueIds,
  type CommunicationAudienceFilters,
  type RegisteredAttendee,
} from "./match.pure";

export type {
  CommunicationAudienceFilters,
  RegisteredAttendee,
} from "./match.pure";
export {
  attendeeMatchesCampaign,
  buyerMatchesFilters,
  canonicalizeAudienceIds,
  categoryMatches,
  collectAttendeeFromItems,
  countAudienceOverlap,
  includeBuyerPool,
  includeGroupPool,
  includeRegisteredPool,
  isEntityAudienceContact,
  mergeAudienceIds,
  orderCompletedProfileIds,
  pickEntityAudienceProfileIds,
  profileMatchesFilters,
  profileMatchesSources,
  uniqueIds,
} from "./match.pure";

export type AudienceResolveInput = CommunicationAudienceFilters & {
  eventId: string;
};

export type AudienceResolveResult = {
  profileIds: string[];
  registeredCount: number;
  audienceSize: number;
  buyerCount: number;
  groupMemberCount: number;
  overlapCount: number;
};

type CommunicationDatabaseClient = typeof prisma;

type ConfirmedItemRow = {
  ticketId: string | null;
  invitationAttendee: {
    contactId: string | null;
    invitationId: string;
  } | null;
  order: { profileId: string | null };
};

function canonicalProfileId(item: ConfirmedItemRow): string | null {
  return item.invitationAttendee?.contactId ?? item.order.profileId ?? null;
}

function groupRegisteredAttendees(
  items: ConfirmedItemRow[]
): Map<string, RegisteredAttendee> {
  const itemsByProfile = new Map<
    string,
    Array<{ ticketId?: string | null; invitationId?: string | null }>
  >();

  for (const item of items) {
    const profileId = canonicalProfileId(item);
    if (!profileId) continue;
    const list = itemsByProfile.get(profileId) ?? [];
    list.push({
      ticketId: item.ticketId,
      invitationId: item.invitationAttendee?.invitationId,
    });
    itemsByProfile.set(profileId, list);
  }

  const attendees = new Map<string, RegisteredAttendee>();
  for (const [profileId, profileItems] of itemsByProfile) {
    attendees.set(profileId, collectAttendeeFromItems(profileId, profileItems));
  }
  return attendees;
}

async function loadConfirmedItems(
  eventId: string,
  db: CommunicationDatabaseClient
): Promise<ConfirmedItemRow[]> {
  return db.orderItem.findMany({
    where: {
      status: OrderItemStatus.CONFIRMED,
      order: {
        eventId,
        status: OrderStatus.COMPLETED,
      },
    },
    select: {
      ticketId: true,
      invitationAttendee: {
        select: { contactId: true, invitationId: true },
      },
      order: { select: { profileId: true } },
    },
  });
}

async function loadBuyerIds(
  eventId: string,
  filters: CommunicationAudienceFilters,
  db: CommunicationDatabaseClient
): Promise<string[]> {
  const items = await loadConfirmedItems(eventId, db);
  return [...groupRegisteredAttendees(items).values()]
    .filter((attendee) => buyerMatchesFilters(attendee, filters))
    .map((attendee) => attendee.profileId);
}

const entityTeamSelect = (eventId: string) => ({
  email: true,
  team: {
    select: {
      id: true,
      email: true,
      profiles: {
        where: { eventId },
        select: { id: true },
        take: 1,
        orderBy: { createdAt: "desc" as const },
      },
    },
  },
});

function mapEntityTeam(
  entities: Array<{
    email: string | null;
    team: Array<{
      id: string;
      email: string | null;
      profiles: Array<{ id: string }>;
    }>;
  }>
) {
  return entities.map((entity) => ({
    email: entity.email,
    team: entity.team.map((member) => ({
      id: member.id,
      email: member.email,
      profileId: member.profiles[0]?.id ?? null,
    })),
  }));
}

async function loadEntityTeamProfileIds(
  eventId: string,
  kind: "exhibitor" | "sponsor",
  groupIds: string[],
  includeTeamMembers: boolean,
  db: CommunicationDatabaseClient
): Promise<string[]> {
  if (groupIds.length === 0) return [];
  const entities =
    kind === "exhibitor"
      ? await db.exhibitor.findMany({
          where: { eventId, participantGroupId: { in: groupIds } },
          select: entityTeamSelect(eventId),
        })
      : await db.sponsor.findMany({
          where: { eventId, participantGroupId: { in: groupIds } },
          select: entityTeamSelect(eventId),
        });
  return pickEntityAudienceProfileIds(
    mapEntityTeam(entities),
    includeTeamMembers
  );
}

async function loadGroupMemberIds(
  eventId: string,
  groupIds: string[],
  includeTeamMembers: boolean,
  db: CommunicationDatabaseClient
): Promise<string[]> {
  if (groupIds.length === 0) return [];
  const groups = await db.participantGroup.findMany({
    where: { id: { in: groupIds }, eventId },
    select: { id: true, type: true },
  });
  const profileGroupIds = groups
    .filter(
      (group) =>
        group.type !== ParticipantGroupType.EXHIBITORS &&
        group.type !== ParticipantGroupType.SPONSORS
    )
    .map((group) => group.id);
  const exhibitorGroupIds = groups
    .filter((group) => group.type === ParticipantGroupType.EXHIBITORS)
    .map((group) => group.id);
  const sponsorGroupIds = groups
    .filter((group) => group.type === ParticipantGroupType.SPONSORS)
    .map((group) => group.id);

  const [fromProfiles, exhibitorProfileIds, sponsorProfileIds] =
    await Promise.all([
      profileGroupIds.length
        ? db.profile.findMany({
            where: {
              eventId,
              participantGroupIds: { hasSome: profileGroupIds },
            },
            select: { id: true },
          })
        : [],
      loadEntityTeamProfileIds(
        eventId,
        "exhibitor",
        exhibitorGroupIds,
        includeTeamMembers,
        db
      ),
      loadEntityTeamProfileIds(
        eventId,
        "sponsor",
        sponsorGroupIds,
        includeTeamMembers,
        db
      ),
    ]);

  return uniqueIds([
    ...fromProfiles.map((profile) => profile.id),
    ...exhibitorProfileIds,
    ...sponsorProfileIds,
  ]);
}

async function loadProfileGroupIds(
  eventId: string,
  profileId: string,
  includeTeamMembers: boolean,
  db: CommunicationDatabaseClient
): Promise<string[]> {
  const profile = await db.profile.findFirst({
    where: { id: profileId, eventId },
    select: { participantGroupIds: true, userId: true },
  });
  if (!profile) return [];
  if (!profile.userId) return profile.participantGroupIds;

  const entitySelect = {
    participantGroupId: true,
    ...entityTeamSelect(eventId),
  };
  const [exhibitors, sponsors] = await Promise.all([
    db.exhibitor.findMany({
      where: { eventId, teamIds: { has: profile.userId } },
      select: entitySelect,
    }),
    db.sponsor.findMany({
      where: { eventId, teamIds: { has: profile.userId } },
      select: entitySelect,
    }),
  ]);

  const entityGroupIds = [...exhibitors, ...sponsors]
    .filter((entity) =>
      isEntityAudienceContact(
        mapEntityTeam([entity])[0],
        profile.userId,
        includeTeamMembers
      )
    )
    .map((entity) => entity.participantGroupId);

  return uniqueIds([...profile.participantGroupIds, ...entityGroupIds]);
}

async function canonicalizeToLoginProfiles(
  eventId: string,
  profileIds: string[],
  db: CommunicationDatabaseClient
): Promise<string[]> {
  if (profileIds.length === 0) return profileIds;
  const [event, profiles] = await Promise.all([
    db.event.findUnique({
      where: { id: eventId },
      select: { whiteLabelId: true },
    }),
    db.profile.findMany({
      where: { id: { in: profileIds } },
      select: { id: true, email: true },
    }),
  ]);
  if (!event?.whiteLabelId) return uniqueIds(profileIds);

  const emails = uniqueIds(
    profiles.map((profile) => profile.email?.trim().toLowerCase())
  );
  if (emails.length === 0) return uniqueIds(profileIds);

  const loginUsers = await db.user.findMany({
    where: {
      accessibleWhiteLabelId: event.whiteLabelId,
      email: { in: emails },
    },
    select: { id: true, email: true },
  });
  if (loginUsers.length === 0) return uniqueIds(profileIds);

  const loginProfiles = await db.profile.findMany({
    where: {
      eventId,
      userId: { in: loginUsers.map((user) => user.id) },
    },
    select: { id: true, userId: true, createdAt: true },
    orderBy: { createdAt: "desc" },
  });
  const profileByUserId = new Map<string, string>();
  for (const profile of loginProfiles) {
    if (profile.userId && !profileByUserId.has(profile.userId)) {
      profileByUserId.set(profile.userId, profile.id);
    }
  }
  const loginProfileIdByEmail: Record<string, string> = {};
  for (const user of loginUsers) {
    const profileId = profileByUserId.get(user.id);
    const email = user.email?.trim().toLowerCase();
    if (profileId && email) loginProfileIdByEmail[email] = profileId;
  }
  return canonicalizeAudienceIds(profiles, loginProfileIdByEmail, profileIds);
}

export async function resolveRegisteredAudience(
  input: AudienceResolveInput,
  db: CommunicationDatabaseClient = prisma
): Promise<AudienceResolveResult> {
  const [buyerIds, groupMemberIds] = await Promise.all([
    includeBuyerPool(input) ? loadBuyerIds(input.eventId, input, db) : [],
    includeGroupPool(input)
      ? loadGroupMemberIds(
          input.eventId,
          input.participantGroupIds ?? [],
          Boolean(input.includeEntityTeamMembers),
          db
        )
      : [],
  ]);

  const registeredIds = uniqueIds([...buyerIds, ...groupMemberIds]);
  const profileIds = await canonicalizeToLoginProfiles(
    input.eventId,
    mergeAudienceIds(registeredIds, input.manualProfileIds),
    db
  );
  return {
    profileIds,
    registeredCount: registeredIds.length,
    audienceSize: profileIds.length,
    buyerCount: buyerIds.length,
    groupMemberCount: groupMemberIds.length,
    overlapCount: countAudienceOverlap(buyerIds, groupMemberIds),
  };
}

export async function loadRegisteredAttendee(
  eventId: string,
  profileId: string,
  db: CommunicationDatabaseClient = prisma
): Promise<RegisteredAttendee | null> {
  const items = await db.orderItem.findMany({
    where: {
      status: OrderItemStatus.CONFIRMED,
      order: { eventId, status: OrderStatus.COMPLETED },
      OR: [
        { order: { profileId } },
        { invitationAttendee: { contactId: profileId } },
      ],
    },
    select: {
      ticketId: true,
      invitationAttendee: {
        select: { contactId: true, invitationId: true },
      },
      order: { select: { profileId: true } },
    },
  });

  const ownItems = items.filter(
    (item) => canonicalProfileId(item) === profileId
  );
  if (ownItems.length === 0) return null;

  return collectAttendeeFromItems(
    profileId,
    ownItems.map((item) => ({
      ticketId: item.ticketId,
      invitationId: item.invitationAttendee?.invitationId,
    }))
  );
}

export async function profileMatchesAudience(
  eventId: string,
  profileId: string,
  filters: CommunicationAudienceFilters,
  db: CommunicationDatabaseClient = prisma
): Promise<boolean> {
  if (filters.manualProfileIds?.includes(profileId)) return true;

  const needBuyer = includeBuyerPool(filters);
  const needGroup = includeGroupPool(filters);
  const [attendee, groupIds] = await Promise.all([
    needBuyer ? loadRegisteredAttendee(eventId, profileId, db) : null,
    needGroup
      ? loadProfileGroupIds(
          eventId,
          profileId,
          Boolean(filters.includeEntityTeamMembers),
          db
        )
      : [],
  ]);

  return attendeeMatchesCampaign(profileId, attendee, filters, groupIds);
}

export function filtersFromSettings(settings: {
  audienceTicketIds?: string[];
  audienceInvitationIds?: string[];
  audienceParticipantGroupIds?: string[];
  manualAudienceProfileIds?: string[];
  audienceIncludeEntityTeamMembers?: boolean;
  includeRegisteredAttendees?: boolean;
}): CommunicationAudienceFilters {
  return {
    ticketIds: settings.audienceTicketIds,
    invitationIds: settings.audienceInvitationIds,
    participantGroupIds: settings.audienceParticipantGroupIds,
    manualProfileIds: settings.manualAudienceProfileIds,
    includeEntityTeamMembers: Boolean(
      settings.audienceIncludeEntityTeamMembers
    ),
    includeRegisteredAttendees: settings.includeRegisteredAttendees !== false,
  };
}
